import {
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonLabel,
  IonRow,
  IonToolbar,
  useIonViewWillEnter,
} from '@ionic/react'
import React, { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { IRootState } from '../../../../store'
import { saveVitals, setSedationScale } from '../../store/vitals.actions'
import { initialSedationScaleState } from '../../store/vitals.reducers'
import { getVitalOptionsSelector } from '../../../options/selectors'
import { IVitalOptionsState } from '../../../options/store/options.types'
import { useTranslation } from 'react-i18next'
import { AccordianComponent } from '../../../../components/accordian/Accordian.components'
import { IScaleOption, IScaleSelection, ISedationScale } from '../../store/vitals.types'
import { scaleCombinedTotals, scaleSectionTotal } from '../../utils/vitals.util'
import SingleSelect from '../../../../components/singleSelect/SingleSelect.component'
import './VitalsSedationScale.content.css'
import { useHistory } from 'react-router'

const VitalsSedationScaleContent: React.FC = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { t } = useTranslation()
  const sedationScale: ISedationScale = useSelector((state: IRootState) => state.vitals.sedationScale)
  const { selections } = sedationScale ?? { selections: [] }
  const vitalOptions: IVitalOptionsState = useSelector(getVitalOptionsSelector)
  const { sedationScaleOptions } = vitalOptions

  useIonViewWillEnter(() => {
    if (!sedationScale) {
      dispatch(setSedationScale({ ...initialSedationScaleState }))
    }
  })

  const setSelectedOptionForSection = (scaleId: number, sectionLabel: string, option: IScaleOption | null) => {
    const nextSelections = selections.filter((s) => !(s.scaleId === scaleId && s.sectionLabel === sectionLabel))
    if (option) {
      const optionToAdd: IScaleSelection = {
        scaleId,
        sectionLabel,
        id: option.id,
        name: option.name,
        score: option.score,
      }
      nextSelections.push(optionToAdd)
    }

    const { totalsCombined } = scaleCombinedTotals(nextSelections, sedationScaleOptions)
    dispatch(setSedationScale({ totalsCombined, selections: nextSelections }))
  }

  const getSelectedOption = useCallback(
    (options: IScaleOption[], scaleId: number) => {
      const selection = selections.find((selection) => selection.scaleId === scaleId)
      return options.find((option) => option.id === selection?.id)
    },
    [selections]
  )

  const hasSedationScaleData = (sedationScale?.selections?.length ?? 0) > 0 || Boolean(sedationScale?.totalsCombined)

  const handleClear = () => {
    dispatch(setSedationScale({ ...initialSedationScaleState }))
  }

  const mainContent = () => {
    return sedationScaleOptions.map((scale) => {
      const scaleT = scaleSectionTotal(scale, selections)
      const hasAnySelection = scaleT !== null
      const displayTotal = scaleT ?? 'N/A'

      return (
        <AccordianComponent
          key={`${scale.scaleId}_${scale.scaleLabel}`}
          heading={scale.scaleLabel}
          isCheckMark={hasAnySelection}
          content={
            <>
              {scale.sections?.map((section, index) => (
                <React.Fragment key={`${scale.scaleId}_${section.sectionLabel}`}>
                  <IonRow>
                    <IonCol className='picklist'>
                      <SingleSelect
                        disabled={false}
                        dataTestID={`SedationScale_${scale.scaleId}_${section.sectionLabel}`}
                        options={section.options}
                        selectedOption={getSelectedOption(section.options, scale.scaleId)}
                        listHeader={section.sectionLabel}
                        label={section.sectionLabel}
                        showSelectedOptionId='score'
                        action={(selectedOption) => {
                          setSelectedOptionForSection(
                            scale.scaleId,
                            section.sectionLabel,
                            selectedOption as IScaleOption | null
                          )
                        }}
                      />
                    </IonCol>
                  </IonRow>
                  {index < scale.sections.length - 1 && <hr className='field-divider' />}
                </React.Fragment>
              ))}

              <hr className='field-divider' />
              <IonRow className='sedation-scale-total-row ion-align-items-center ion-justify-content-between'>
                <IonCol>
                  <IonLabel color='medium'>{t('vitals.sedationScale.total')}</IonLabel>
                </IonCol>
                <IonCol size='auto'>
                  <IonLabel className='sedation-scale-total-value'>{displayTotal}</IonLabel>
                </IonCol>
              </IonRow>
            </>
          }
        />
      )
    })
  }

  return (
    <>
      <IonContent className='ion-margin-bottom' fullscreen id='VitalsSedationScaleContent'>
        <IonRow className='ion-padding-start ion-padding-end ion-padding-bottom'>
          <IonCol size='12' className='sedation-scale-content'>
            <IonGrid className='ion-no-padding'>
              {sedationScaleOptions.length === 0 ? (
                <IonRow className='ion-align-items-center sedation-scale-no-data-row'>
                  <IonCol>
                    <IonLabel color='medium' className='sedation-scale-no-data-label'>
                      {t('vitals.sedationScale.noData')}
                    </IonLabel>
                  </IonCol>
                </IonRow>
              ) : (
                mainContent()
              )}
            </IonGrid>
          </IonCol>
        </IonRow>
      </IonContent>
      <IonFooter>
        <IonToolbar color='light' className='p-10'>
          <IonButtons className='btn-grps'>
            <IonButton
              className='btn btn-tablet'
              color='danger'
              fill='outline'
              expand='block'
              disabled={!hasSedationScaleData}
              onClick={handleClear}>
              {t('common.buttons.Clear')}
            </IonButton>
            <IonButton
              className='btn btn-tablet btn-primary'
              expand='block'
              disabled={!hasSedationScaleData}
              onClick={() => {
                dispatch(saveVitals())
                history.back()
              }}>
              {t('common.buttons.Save')}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonFooter>
    </>
  )
}

export default VitalsSedationScaleContent
